// L'état d'une run, chargé UNE fois puis tenu en mémoire (§2.1).
//
// Trois propriétés que l'écran doit à ce fichier :
//   • chargement unique — la journée entière arrive en une requête ;
//   • navigation en mémoire — changer d'étape est un changement d'état ;
//   • écritures optimistes — l'écran répond avant le serveur, et revient en
//     arrière si l'écriture échoue. Tu n'attends jamais une sauvegarde.
//
// Mode dégradé (§9.3) : une run DÉJÀ OUVERTE continue de fonctionner sans
// réseau. Les écritures s'empilent dans une file locale et repartent au retour.
// Interdits hors ligne : clôturer la run, armer une session Honeywell.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import * as api from './api'
import { supabase } from '@/lib/supabase'
import { messageErreur, jetonErreur } from '@/lib/errors'
import { useAuth } from '@/lib/auth'
import { useToast } from '@/ui/Toast'
import { sonValidation } from '@/lib/sons'
import { etatVerrou, type EtatVerrou } from '@/domain/parcours'
import { HEARTBEAT_MS } from '@/domain/steps'
import type { Etape, Journee, Run, PadAttente, Consigne } from '@/domain/types'

interface EnAttente { id: number; libelle: string; rejouer: () => Promise<unknown> }

interface RunCtx {
  date: string
  journee: Journee | null
  run: Run | null
  etapes: Etape[]
  padAttente: PadAttente[]
  consignes: Consigne[]
  chargement: boolean
  erreur: string | null
  verrou: EtatVerrou
  enLigne: boolean
  attente: number
  recharger: () => Promise<void>
  activer: (code: string) => Promise<void>
  valider: (code: string, force?: boolean) => Promise<void>
  mettreDeCote: (code: string, motif: string) => Promise<void>
  repondre: (code: string, oui: boolean) => Promise<void>
  rouvrir: (code: string) => Promise<void>
  brancher: (code: string, br: 'dep' | 'sco',
             statut: 'en_cours' | 'validee' | 'skip_question', force?: boolean) => Promise<void>
  prendreMain: (force?: boolean) => Promise<void>
  liberer: () => Promise<void>
  cloturer: () => Promise<Record<string, number>>
  rouvrirRun: () => Promise<void>
  recalculerRegles: () => Promise<void>
  noter: (texte: string, etape?: string | null) => Promise<void>
  padResoudre: (id: string, statut: PadAttente['statut'], note?: string) => Promise<void>
  padAjouter: (ref: string, paymentId: string | null, montant: number) => Promise<void>
  /** Reflète localement une écriture faite par un RPC V2 sur `rapport`. */
  majRapport: (patch: Record<string, unknown>) => void
  /**
   * Écriture optimiste dans le blob V2 : l'écran bouge tout de suite, la RPC
   * suit, et l'état d'avant revient si elle refuse. C'est le chemin unique des
   * écrans d'étape — aucun d'eux n'appelle Supabase directement.
   */
  ecrireRapport: (
    libelle: string,
    patch: Record<string, unknown>,
    envoyer: () => Promise<unknown>,
    etape?: string,
  ) => Promise<void>
}

const Ctx = createContext<RunCtx | null>(null)

export function RunProvider({ date, children }: { date: string; children: ReactNode }) {
  const { user } = useAuth()
  const toast = useToast()

  const [journee, setJournee] = useState<Journee | null>(null)
  const [run, setRun] = useState<Run | null>(null)
  const [etapes, setEtapes] = useState<Etape[]>([])
  const [padAttente, setPadAttente] = useState<PadAttente[]>([])
  const [consignes, setConsignes] = useState<Consigne[]>([])
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enLigne, setEnLigne] = useState(() => navigator.onLine !== false)
  const [file, setFile] = useState<EnAttente[]>([])
  const seq = useRef(0)
  const rejoue = useRef(false)

  // ── Chargement unique ────────────────────────────────────────────────────
  const recharger = useCallback(async () => {
    try {
      await api.ouvrirRun(date)
      const [j, r, e, p, c] = await Promise.all([
        api.chargerJournee(date),
        api.chargerRun(date),
        api.chargerEtapes(date),
        api.chargerPadAttente('en_attente'),
        api.chargerConsignes([date]),
      ])
      setJournee(j); setRun(r); setEtapes(e); setPadAttente(p); setConsignes(c)
      setErreur(null)
    } catch (err) {
      setErreur(messageErreur(err))
    } finally {
      setChargement(false)
    }
  }, [date])

  useEffect(() => { setChargement(true); void recharger() }, [recharger])

  // ── Temps réel : une autre personne, ou la V2, a bougé quelque chose ─────
  useEffect(() => {
    const canal = supabase.channel(`fc-run-${date}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'fc_etapes', filter: `run_date=eq.${date}` },
        (p) => {
          const ligne = p.new as Etape | undefined
          if (!ligne?.code) return
          setEtapes((prev) => prev.map((e) => (e.code === ligne.code ? { ...e, ...ligne } : e)))
        })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'fc_runs', filter: `journee_date=eq.${date}` },
        (p) => setRun((prev) => ({ ...(prev ?? {}), ...(p.new as Run) })))
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'journees', filter: `date=eq.${date}` },
        (p) => setJournee((prev) => ({ ...(prev ?? {}), ...(p.new as Journee) })))
      .subscribe()
    return () => { void supabase.removeChannel(canal) }
  }, [date])

  // ── Hors ligne : on empile, on rejoue au retour ──────────────────────────
  useEffect(() => {
    const on = () => setEnLigne(true)
    const off = () => setEnLigne(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off) }
  }, [])

  useEffect(() => {
    if (!enLigne || file.length === 0 || rejoue.current) return
    rejoue.current = true
    const lot = file
    void (async () => {
      let ok = 0
      for (const item of lot) {
        try { await item.rejouer(); ok++ } catch (err) { toast(messageErreur(err), true) }
      }
      setFile((f) => f.filter((x) => !lot.some((l) => l.id === x.id)))
      if (ok > 0) toast(`${ok} MODIFICATION${ok > 1 ? 'S' : ''} RESYNCHRONISÉE${ok > 1 ? 'S' : ''}`)
      await recharger()
      rejoue.current = false
    })()
  }, [enLigne, file, toast, recharger])

  /**
   * Écriture optimiste : l'écran bouge tout de suite, le serveur suit.
   * En cas d'échec réseau, l'action part dans la file locale ; en cas de refus
   * métier (verrou, clôture), on remet l'état d'avant et on dit pourquoi.
   */
  const ecrire = useCallback(async (
    libelle: string,
    optimiste: () => void,
    annuler: () => void,
    envoyer: () => Promise<unknown>,
  ) => {
    optimiste()
    if (navigator.onLine === false) {
      setFile((f) => [...f, { id: ++seq.current, libelle, rejouer: envoyer }])
      return
    }
    try {
      await envoyer()
    } catch (err) {
      const jeton = jetonErreur(err)
      const reseau = /Failed to fetch|NetworkError|fetch failed/i.test(jeton)
      if (reseau) {
        setFile((f) => [...f, { id: ++seq.current, libelle, rejouer: envoyer }])
        setEnLigne(false)
        return
      }
      annuler()
      toast(messageErreur(err), true)
      throw err
    }
  }, [toast])

  const patcher = useCallback((code: string, patch: Partial<Etape>) => {
    setEtapes((prev) => prev.map((e) => (e.code === code ? { ...e, ...patch } : e)))
  }, [])

  const avant = useCallback((code: string): Etape | undefined =>
    etapes.find((e) => e.code === code), [etapes])

  // ── Actions du parcours ──────────────────────────────────────────────────

  const activer = useCallback(async (code: string) => {
    const e = avant(code)
    if (!e || e.statut !== 'a_faire') {
      // Simple signal de vie : sans intérêt s'il échoue.
      void api.toucherEtape(date, code).catch(() => {})
      return
    }
    const copie = { ...e }
    await ecrire(`entrée dans l'étape ${code}`,
      () => patcher(code, { statut: 'en_cours', derniere_activite: new Date().toISOString() }),
      () => patcher(code, copie),
      () => api.activerEtape(date, code))
  }, [avant, date, ecrire, patcher])

  const valider = useCallback(async (code: string, force = false) => {
    const e = avant(code)
    if (!e) return
    const copie = { ...e }
    await ecrire(`validation de l'étape ${code}`,
      () => patcher(code, {
        statut: 'validee', force_valide: force,
        valide_at: new Date().toISOString(),
        valide_par_nom: (user?.app_metadata?.first_name as string | undefined)
          ? `${user?.app_metadata?.first_name as string} ${user?.app_metadata?.last_name as string}`
          : (user?.email ?? null),
      }),
      () => patcher(code, copie),
      () => api.validerEtape(date, code, force))
    sonValidation()
  }, [avant, date, ecrire, patcher, user])

  const mettreDeCote = useCallback(async (code: string, motif: string) => {
    const e = avant(code)
    if (!e) return
    const copie = { ...e }
    await ecrire(`mise de côté de l'étape ${code}`,
      () => patcher(code, { statut: 'de_cote', motif }),
      () => patcher(code, copie),
      () => api.mettreDeCote(date, code, motif))
  }, [avant, date, ecrire, patcher])

  const repondre = useCallback(async (code: string, oui: boolean) => {
    const e = avant(code)
    if (!e) return
    const copie = { ...e }
    await ecrire(`réponse à la question de l'étape ${code}`,
      () => patcher(code, oui
        ? { statut: 'en_cours', motif: null }
        : { statut: 'skip_question', motif: 'Réponse « non » à la question d’entrée' }),
      () => patcher(code, copie),
      () => api.repondreQuestion(date, code, oui))
  }, [avant, date, ecrire, patcher])

  const rouvrir = useCallback(async (code: string) => {
    const e = avant(code)
    if (!e) return
    const copie = { ...e }
    await ecrire(`réouverture de l'étape ${code}`,
      () => patcher(code, {
        statut: 'en_cours', valide_at: null, valide_par_nom: null,
        force_valide: false, motif: null, motif_regle: null,
      }),
      () => patcher(code, copie),
      () => api.rouvrirEtape(date, code))
  }, [avant, date, ecrire, patcher])

  const brancher = useCallback(async (
    code: string, br: 'dep' | 'sco',
    statut: 'en_cours' | 'validee' | 'skip_question', force = false,
  ) => {
    const e = avant(code)
    if (!e) return
    const copie = { ...e }
    const branche = { ...e.branche, [br]: { statut, at: new Date().toISOString() } }
    const soldee = (s?: { statut: string }) => s?.statut === 'validee' || s?.statut === 'skip_question'
    const toutes = soldee(branche.dep) && soldee(branche.sco)
    await ecrire(`branche ${br} de l'étape ${code}`,
      () => patcher(code, {
        branche,
        statut: toutes ? 'validee' : e.statut === 'a_faire' ? 'en_cours' : e.statut,
        valide_at: toutes ? new Date().toISOString() : e.valide_at,
      }),
      () => patcher(code, copie),
      () => api.majBranche(date, code, br, statut, force))
    if (statut === 'validee') sonValidation()
  }, [avant, date, ecrire, patcher])

  // ── Verrou ───────────────────────────────────────────────────────────────

  const prendreMain = useCallback(async (force = false) => {
    const r = await api.prendreMain(date, force)
    setRun(r)
  }, [date])

  const liberer = useCallback(async () => {
    try { setRun(await api.libererRun(date)) } catch { /* sortie : sans conséquence */ }
  }, [date])

  const cloturer = useCallback(async () => {
    if (navigator.onLine === false) {
      // Interdit hors ligne : la confirmation serveur est requise (§9.3).
      throw new Error('hors_ligne_cloture')
    }
    const { run: r, stats } = await api.cloturerRun(date)
    setRun(r)
    await recharger()
    return stats
  }, [date, recharger])

  const rouvrirRun = useCallback(async () => {
    // Comme la clôture : réservé en ligne, car le serveur doit reconfirmer le
    // quota de journées ouvertes avant de rebasculer la run.
    if (navigator.onLine === false) throw new Error('hors_ligne_cloture')
    const { run: r } = await api.rouvrirRun(date)
    setRun(r)
    await recharger()
  }, [date, recharger])

  const recalculerRegles = useCallback(async () => {
    await api.recalculerRegles(date)
    setEtapes(await api.chargerEtapes(date))
  }, [date])

  const noter = useCallback(async (texte: string, etape?: string | null) => {
    await api.noterJournal(date, texte, etape ?? null)
  }, [date])

  const padResoudre = useCallback(async (id: string, statut: PadAttente['statut'], note?: string) => {
    const copie = padAttente
    await ecrire('résolution d’un PAD en attente',
      () => setPadAttente((p) => p.filter((x) => x.id !== id || statut === 'en_attente')),
      () => setPadAttente(copie),
      () => api.resoudrePadAttente(id, statut, note))
  }, [ecrire, padAttente])

  const majRapport = useCallback((patch: Record<string, unknown>) => {
    setJournee((j) => (j ? { ...j, rapport: { ...(j.rapport ?? {}), ...patch } } : j))
  }, [])

  const ecrireRapport = useCallback(async (
    libelle: string,
    patch: Record<string, unknown>,
    envoyer: () => Promise<unknown>,
    etape?: string,
  ) => {
    const avantRapport = journee?.rapport ?? null
    await ecrire(libelle,
      () => majRapport(patch),
      () => setJournee((j) => (j ? { ...j, rapport: avantRapport } : j)),
      envoyer)
    // La dernière activité de l'étape avance : c'est elle qui alimente le
    // bandeau de reprise après interruption.
    if (etape) void api.toucherEtape(date, etape).catch(() => {})
  }, [date, ecrire, journee, majRapport])

  const padAjouter = useCallback(async (ref: string, paymentId: string | null, montant: number) => {
    const ligne = await api.ajouterPadAttente(date, ref, paymentId, montant)
    setPadAttente((p) => [ligne, ...p.filter((x) => x.id !== ligne.id)])
  }, [date])

  // ── Battement de cœur : prolonge le verrou, alimente le chrono ───────────
  const jeTiensLeVerrou = run?.lock_user != null && run.lock_user === (user?.id ?? null)
  useEffect(() => {
    if (!jeTiensLeVerrou || run?.statut !== 'ouverte') return
    const battre = () => {
      if (document.visibilityState !== 'visible' || navigator.onLine === false) return
      api.battement(date).then(setRun).catch(() => {})
    }
    const t = window.setInterval(battre, HEARTBEAT_MS)
    return () => window.clearInterval(t)
  }, [date, jeTiensLeVerrou, run?.statut])

  // Sortir de la run rend la main tout de suite — sans attendre les 15 minutes.
  useEffect(() => () => { if (jeTiensLeVerrou) void api.libererRun(date).catch(() => {}) },
    [date, jeTiensLeVerrou])

  const verrou = useMemo(() => etatVerrou(run, user?.id ?? null), [run, user])

  const valeur: RunCtx = {
    date, journee, run, etapes, padAttente, consignes, chargement, erreur,
    verrou, enLigne, attente: file.length,
    recharger, activer, valider, mettreDeCote, repondre, rouvrir, brancher,
    prendreMain, liberer, cloturer, rouvrirRun, recalculerRegles, noter, padResoudre, padAjouter,
    majRapport, ecrireRapport,
  }
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>
}

export function useRun(): RunCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useRun hors RunProvider')
  return c
}
